import { useEffect, useState } from "react";

import { Bell, Clock3 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { cn } from "../../lib/utils";
import { useApi } from "../../context/ApiContext";
import { Button } from "../ui/button.jsx";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";

const NotificationBell = ({
  count,
  title = "Notifications",
  ariaLabel = "Notifications",
  className,
  badgeClassName,
  showZero = true,
  onOverdueClick
}) => {
  const navigate = useNavigate();
  const api = useApi();
  const hasExternalCount = count !== undefined && count !== null;
  const [internalCount, setInternalCount] = useState(0);
  const safeCount = hasExternalCount
    ? (Number.isFinite(Number(count)) ? Number(count) : 0)
    : internalCount;
  const shouldShowBadge = showZero || safeCount > 0;
  const [open, setOpen] = useState(false);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchOverdueCount = async () => {
      if (hasExternalCount) return;

      try {
        const activeProjectId = localStorage.getItem("activeProjectId");
        const params = {
          page: 0,
          size: 1
        };

        if (activeProjectId && activeProjectId !== "ALL") {
          params.projectId = activeProjectId;
        }

        const response = await api.get("/tasks", { params });
        const counts = response.data?.statusCounts || response.data?.metadata?.statusCounts;
        setInternalCount(Number(counts?.OVERDUE || 0));
      } catch (error) {
        setInternalCount(0);
      }
    };

    fetchOverdueCount();
  }, [api, hasExternalCount, open]);

  useEffect(() => {
    const fetchOverdueTasks = async () => {
      if (!open) return;

      setIsLoading(true);
      try {
        const activeProjectId = localStorage.getItem("activeProjectId");
        const params = {
          status: "OVERDUE",
          page: 0,
          size: 5,
          sortBy: "dueDate",
          direction: "asc"
        };

        if (activeProjectId && activeProjectId !== "ALL") {
          params.projectId = activeProjectId;
        }

        const response = await api.get("/tasks", { params });
        setOverdueTasks(response.data?.items || []);
      } catch (error) {
        setOverdueTasks([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOverdueTasks();
  }, [open, api]);

  const handleOverdueClick = () => {
    if (onOverdueClick) {
      onOverdueClick(safeCount);
      return;
    }
    navigate("/tasks?filter=OVERDUE");
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className={cn("relative h-10 w-10 shrink-0", className)}
          aria-label={ariaLabel}
          title={title}
        >
          <Bell className="h-4 w-4" />
          {shouldShowBadge && (
            <span
              className={cn(
                "absolute -top-1.5 -right-1.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-500 text-white text-[10px] leading-none flex items-center justify-center font-semibold",
                badgeClassName
              )}
            >
              {safeCount > 99 ? "99+" : safeCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-sm font-semibold">Notification</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {isLoading ? (
          <div className="px-2 py-2 text-sm text-muted-foreground">Loading...</div>
        ) : overdueTasks.length === 0 ? (
          <div className="px-2 py-2 text-sm text-muted-foreground">No overdue tasks</div>
        ) : (
          overdueTasks.map((task) => (
            <DropdownMenuItem
              key={task.id}
              onClick={handleOverdueClick}
              className="cursor-pointer flex items-start gap-2 py-2"
            >
              <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                <Clock3 className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
              </span>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-medium text-foreground truncate">{task.title || "Untitled Task"}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {task.project?.name || "No Project"}
                </span>
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
